import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sessionExpired, setSessionExpired] = useState(false);

  const forceLogout = useCallback((message) => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setSessionExpired(message || 'تم تسجيل الدخول من جهاز آخر');
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (error) {
          // التحقق من تسجيل الدخول من جهاز آخر
          if (error.response?.data?.code === 'SESSION_EXPIRED') {
            forceLogout(error.response.data.message);
          } else {
            // محاولة تجديد التوكن تلقائياً
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              try {
                const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken });
                const newToken = refreshResponse.data.token;
                const newRefreshToken = refreshResponse.data.refreshToken;
                setToken(newToken);
                localStorage.setItem('token', newToken);
                localStorage.setItem('refreshToken', newRefreshToken);

                const userResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
                  headers: { Authorization: `Bearer ${newToken}` },
                });
                setUser(userResponse.data);
              } catch {
                setToken(null);
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
              }
            } else {
              setToken(null);
              localStorage.removeItem('token');
            }
          }
        }
      }
      setLoading(false);
    };

    fetchUser();
  }, [token, forceLogout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      email,
      password,
    });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    if (response.data.refreshToken) {
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response.data;
  };

  const register = async (firstNameOrData, lastName, email, password, organizationName, selectedPlan, selectedModules, companyInfo = {}) => {
    // دعم الاستدعاء بكائن واحد أو بمعاملات منفصلة
    let payload;
    if (typeof firstNameOrData === 'object' && firstNameOrData !== null) {
      // استدعاء بكائن واحد (Healthcare / Salon / Tailoring)
      const { plan, ...rest } = firstNameOrData;
      payload = { ...rest, selectedPlan: plan || rest.selectedPlan };
    } else {
      // استدعاء كلاسيكي بمعاملات منفصلة (RegisterPage)
      payload = {
        firstName: firstNameOrData,
        lastName,
        email,
        password,
        organizationName,
        selectedPlan,
        selectedModules,
        subscriptionType: companyInfo?.subscriptionType,
        ...companyInfo,
      };
    }
    const response = await axios.post(`${API_BASE_URL}/auth/register`, payload);
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    if (response.data.refreshToken) {
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response.data;
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // تجاهل أخطاء الخادم عند الخروج
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch {
        // تجاهل الأخطاء
      }
    }
  };

  // اعتراض جميع ردود API للكشف عن SESSION_EXPIRED وتقييد الأقسام وانتهاء الاشتراك
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.data?.code === 'SESSION_EXPIRED') {
          forceLogout(error.response.data.message);
        }
        // معالجة 401 — انتهاء صلاحية التوكن
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login') && !error.config?.url?.includes('/auth/register')) {
          forceLogout('انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.');
        }
        // معالجة انتهاء الاشتراك — 403 مع subscriptionStatus: 'expired'
        if (error.response?.status === 403 && error.response?.data?.subscriptionStatus === 'expired') {
          toast.error('انتهت الفترة التجريبية. يرجى ترقية حسابك للاستمرار.', { duration: 5000, id: 'subscription-expired' });
          // تحديث حالة المنظمة محلياً
          setUser(prev => prev?.organization ? { ...prev, organization: { ...prev.organization, subscriptionStatus: 'inactive' } } : prev);
        }
        // معالجة تقييد الأقسام — 403 مع action: upgrade_or_addon
        if (error.response?.status === 403 && error.response?.data?.action === 'upgrade_or_addon') {
          const msg = error.response.data.message || 'هذا القسم غير مشمول في باقتكم الحالية';
          toast.error(
            `${msg}\n\nيمكنك ترقية الباقة أو إضافة القسم بـ ${error.response.data.addOnPrice || 50} ر.س/شهرياً`,
            { duration: 6000, id: 'module-restricted' }
          );
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [forceLogout]);

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, register, logout, refreshUser, sessionExpired, setSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};
